/**
 * Shared dialog for creating a new seed script workflow.
 * Used by both the Seed Data catalog and the Seed Scripts tab.
 *
 * @module CreateSeedScriptDialog
 */
import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { DialogTrigger, Dialog, DialogContent, DialogFooter, Button, TextField, Flex, Text } from '@chatsdet/ui';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkflowMutations } from '../../hooks/useWorkflowMutations';
import { type SeedDataOutput } from '../../hooks/useSeedWorkflowOutputs';
import { API_URL } from '../../config';
import { SeedOutputsEditor } from './SeedOutputsEditor';

interface CreateSeedScriptDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

export function CreateSeedScriptDialog({ isOpen, onOpenChange }: CreateSeedScriptDialogProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentOrgId = user?.orgId ?? null;
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [outputs, setOutputs] = React.useState<SeedDataOutput[]>([]);

  const { createWorkflowMutation } = useWorkflowMutations({
    currentOrgId,
    workflowId: null,
  });

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    try {
      const result = await createWorkflowMutation.mutateAsync({
        name: trimmedName,
        description: description.trim() || undefined,
        status: 'draft',
        flowType: 'seed',
      });

      const newWorkflowId = result?.workflow?.id;
      if (newWorkflowId) {
        // Save initial outputs if any were defined
        if (outputs.length > 0) {
          const res = await fetch(`${API_URL}/workflows/${newWorkflowId}`, {
            method: 'PUT',
            credentials: 'include',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ seedOutputsJson: outputs }),
          });
          if (!res.ok) {
            console.error('Failed to save initial seed outputs');
          }
        }

        // Auto-create a paired teardown workflow and link it
        try {
          const teardownRes = await fetch(`${API_URL}/workflows`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              name: `Teardown: ${trimmedName}`,
              description: `Cleans up seed data from "${trimmedName}"`,
              status: 'draft',
              flowType: 'teardown',
            }),
          });
          if (teardownRes.ok) {
            const { workflow: teardownWf } = await teardownRes.json();
            await fetch(`${API_URL}/workflows/${newWorkflowId}`, {
              method: 'PUT',
              credentials: 'include',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({ teardownWorkflowId: teardownWf.id }),
            });
          }
        } catch {
          // Teardown creation is best-effort; seed workflow is still usable
          console.error('Failed to create paired teardown workflow');
        }

        onOpenChange(false);
        setName('');
        setDescription('');
        setOutputs([]);
        navigate({
          to: '/flows/$workflowId',
          params: { workflowId: newWorkflowId },
        });
      }
    } catch (error) {
      console.error('Failed to create seed script', error);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setName('');
      setDescription('');
      setOutputs([]);
    }
    onOpenChange(open);
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange} size="xl">
      <span />
      <Dialog title="New Seed Script">
        <DialogContent>
          <Flex direction="column" gap="md">
            <Text size="sm" className="text-muted-foreground">
              Seed scripts provision shared test data before your suites run.
            </Text>
            <TextField
              label="Name"
              value={name}
              onChange={setName}
              placeholder="e.g. Create test users"
              autoFocus
            />
            <TextField
              label="Description"
              value={description}
              onChange={setDescription}
              placeholder="Optional description"
            />
            <Text size="sm" weight="medium">Outputs</Text>
            <Text size="xs" className="text-muted-foreground">
              Declare what data this seed script will produce. You can edit these later.
            </Text>
            <SeedOutputsEditor
              outputs={outputs}
              onChange={setOutputs}
            />
          </Flex>
        </DialogContent>
        <DialogFooter>
          <Button variant="secondary" onPress={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onPress={handleSubmit}
            isDisabled={!name.trim() || createWorkflowMutation.isPending}
          >
            {createWorkflowMutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
